import React, {useState, useCallback, useEffect, useMemo} from "react";
import {inject, observer} from "mobx-react";
import {useLocation, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
//local
import {Subscription} from "./Subscriptions/Subscription";
import {SubscriptionModalOwner} from "./Subscriptions/SubscriptionModalOwner";
import {SubscriptionTabs} from "./Subscriptions/SubscriptionTabs";
import {
  getFullName,
  createLanguageKeysArray,
  subscriptionPrice,
  hasValue,
  updateLocationHash,
} from "helpers/helper";
import {showSuccess} from "helpers/notifications.helper";
import {
  subscriptionTabNumber,
  subscriptionsData,
  buyerSubscriptions,
  sellerSubscriptions,
  allSubscriptions,
  signContractSubscriptions,
  aISubscriptions,
  subscriptionPricePeriods,
} from "constants/user.consts";
import {subscriptionTabs, hashes, subscriptionSubTabHash} from "constants/tabs.const";

export const Subscriptions = inject("store")(
  observer(({store: {organization, clientStore, auth}}) => {
    const {t} = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const {subscriptions, organizationMembers} = organization;
    const {user} = auth;
    const [isSubscriptionModalOwner, setIsSubscriptionModalOwner] = useState(null);
    const hasOrganizationMembers = useMemo(
      () => !!organizationMembers?.length,
      [organizationMembers]
    );
    const [activeTab, setActiveTab] = useState(
      !!location.state?.purchasedSubscription
        ? subscriptionTabNumber[location.state.purchasedSubscription]
        : subscriptionTabs[0].id
    );

    const activeSubcriptions = useMemo(
      () =>
        user?.ActiveOrganization?.SubscribeUnit?.SubscribeProducts?.concat(
          user?.SubscribeUserUnit?.SubscribeProducts
        ).filter(Boolean),
      [user?.ActiveOrganization?.SubscribeUnit, user?.SubscribeUserUnit]
    );
    const initialClientEmail = user?.ActiveOrganization?.paymentEmail;

    const collectDowngradeSubscriptionIds = useCallback(
      (id, ids = []) => {
        const sub = subscriptions.find((item) => item.id === id);
        if (sub?.DowngradeId)
          return collectDowngradeSubscriptionIds(sub?.DowngradeId, [...ids, id]);
        else return [...ids, id];
      },
      [subscriptions]
    );

    const disabledUserSubscriptionIds = useMemo(() => {
      const userSubscriptionIds =
        user?.SubscribeUserUnit?.SubscribeProducts?.map((item) =>
          collectDowngradeSubscriptionIds(item.DowngradeId)
        ) ?? [];
      return userSubscriptionIds.flat();
    }, [user?.SubscribeUserUnit, collectDowngradeSubscriptionIds]);

    const [purchasedUser, setPurchasedUser] = useState(null);

    // enriches api data with static data
    const enrichSubscriptionsData = useCallback(
      (subscriptions) =>
        subscriptions.slice().map((item) => {
          const userSubscription = activeSubcriptions?.find(
            (subscription) => subscription?.id === item.id
          );
          const pricePeriod = (
            userSubscription?.SubscribeUnitProduct ??
            userSubscription?.SubscribeUserUnitProduct
          )?.pricePeriod;
          const pricePeriods = subscriptionPricePeriods
            .map((period) =>
              hasValue(item[period.priceKey]) ? +item[period.priceKey] : null
            )
            .filter(hasValue)
            .sort((a, b) => a - b);

          return {
            ...item,
            ...subscriptionsData[item.name],
            activeStatus: !!userSubscription,
            price: !!userSubscription
              ? subscriptionPrice(userSubscription, pricePeriod)
              : pricePeriods[0],
            hasPeriods: !userSubscription && pricePeriods.length > 1,
            pricePeriod,
          };
        }),
      [activeSubcriptions]
    );

    const regularSubscriptions = !!subscriptions.length
      ? enrichSubscriptionsData(subscriptions).filter((item) => item.allow)
      : subscriptions;

    const getTabSubscriptions = (tabSubscriptions) =>
      !!subscriptions.length
        ? tabSubscriptions?.map(
            (subscription) =>
              regularSubscriptions.find((item) => item.name === subscription) ??
              enrichSubscriptionsData([{name: subscription}])[0]
          )
        : [];

    const subscriptionsSellerTab = getTabSubscriptions(sellerSubscriptions);
    const subscriptionsBuyerTab = getTabSubscriptions(buyerSubscriptions);
    const subscriptionsAllTab = getTabSubscriptions(allSubscriptions);
    const subscriptionsDigitalSigningTab = getTabSubscriptions(signContractSubscriptions);
    const subscriptionsAITab = getTabSubscriptions(aISubscriptions);

    const selectedTabSubscriptions = {
      0: subscriptionsSellerTab,
      1: subscriptionsBuyerTab,
      2: subscriptionsDigitalSigningTab,
      3: subscriptionsAITab,
      4: subscriptionsAllTab,
    };

    const handleChangeSubcription = (id, userId) => {
      const member =
        organizationMembers.find((member) => member.UserId === userId) ?? null;
      setPurchasedUser(member);
      setIsSubscriptionModalOwner(regularSubscriptions.find((item) => item.id === id));
    };

    const handleChangeTab = (tabId) => {
      setActiveTab(tabId);
      const targetHash = `${hashes.SUBSCRIPTION}_${subscriptionTabs[tabId].hash}`;
      updateLocationHash(targetHash);
    };

    const handleCloseModal = () => {
      setPurchasedUser(null);
      setIsSubscriptionModalOwner(null);
    };

    const onSubmit = (values) =>
      (values.userId ? clientStore : organization)
        .setActiveSubscription({
          subscribeProductId: isSubscriptionModalOwner.id,
          ...values,
        })
        .then(() => {
          auth.checkToken(true);
          showSuccess(
            t("SUBSCRIPTION_TAB_SUCCESS_MESSAGE_TEXT"),
            8,
            t("SUBSCRIPTION_TAB_SUCCESS_MESSAGE_TITLE")
          );
        });

    // handle subscription tab change on url hash change
    useEffect(() => {
      if (!!location.state?.purchasedSubscription) {
        const targetTab = subscriptionTabNumber[location.state.purchasedSubscription];
        const targetHash = `${hashes.SUBSCRIPTION}_${subscriptionTabs[targetTab].hash}`;
        setActiveTab(targetTab);
        activeTab === targetTab &&
          navigate(
            {hash: targetHash},
            {
              state: {
                ...location.state,
                purchasedSubscription: undefined,
                purchasedUserId: undefined,
              },
            }
          );
      } else {
        const noSubscriptionSubHash =
          location.hash.split("_")[0] && !location.hash.split("_")[1];
        noSubscriptionSubHash &&
          updateLocationHash(`${hashes.SUBSCRIPTION}_${subscriptionSubTabHash.SELLER}`);
        const tab = location.hash
          ? subscriptionTabs.find((tab) => location.hash.includes(tab.hash))?.id ?? 0
          : 0;
        setActiveTab(tab);
      }
    }, [location.state, navigate, activeTab, location.hash]);

    return (
      <div className="subscriptions__wrapper">
        <div className="header-and-offer">
          <h2 className="subscriptions__title">{t("SUBSCRIPTION_TAB_TITLE_01")}</h2>
          <p>{t("SUBSCRIPTION_TAB_SUBTITLE_01")}</p>

          <SubscriptionTabs
            tabs={subscriptionTabs}
            active={activeTab}
            setActive={handleChangeTab}
          />
        </div>

        {!!subscriptions.length && (
          <React.Fragment>
            <div className="subscriptions__items">
              <div
                className={`subscriptions__items__regular_wrapper __${subscriptionTabs[activeTab].subscriptionsCount}`}
              >
                {selectedTabSubscriptions[activeTab].map((subscription) => (
                  <Subscription
                    key={subscription.id}
                    subscription={subscription}
                    changeSubscription={handleChangeSubcription}
                    getSubscription={organization.getAvailableSubscription}
                    subscriptionsCount={subscriptionTabs[activeTab].subscriptionsCount}
                    options={createLanguageKeysArray(
                      subscription.optionsCount,
                      `${subscription.name}_OPTION`
                    )}
                    currentUser={user}
                    disableGetButton={disabledUserSubscriptionIds.includes(
                      subscription.id
                    )}
                    isLoadedOrganizationMembers={hasOrganizationMembers}
                  />
                ))}
              </div>
            </div>
          </React.Fragment>
        )}

        {isSubscriptionModalOwner && (
          <SubscriptionModalOwner
            currentSubscription={isSubscriptionModalOwner}
            organization={user?.ActiveOrganization}
            username={getFullName(user.firstName, user.lastName)}
            purchasedUser={purchasedUser}
            email={initialClientEmail}
            onClickSubmit={onSubmit}
            onClose={handleCloseModal}
          />
        )}
      </div>
    );
  })
);
